var lib_core = require("lib-core");
var THREE                    = require("three");
var cio_datamodel = require('cio-datamodel');
var UploadStateMachine = require("ui-extensions").UploadStateMachine;
/**
 * @constructor
 */
var ModelUploadStateMachine = function (root, processModel, dataManager, packageViewerModel, config) {
    var self = this;
    self.root = root;
    this.config = config;
    this.processModel = processModel;
    this.dataManager = dataManager;
    this.packageViewerModel = packageViewerModel;
    this.poll = null;
    this.maxFilesAtOnce = config.maxFilesAtOnce || 10;

    UploadStateMachine.call(this, root);
};

lib_core.inherit(ModelUploadStateMachine, UploadStateMachine);

// what file types to accept
ModelUploadStateMachine.prototype.getFileTypes = function(){
    return '.step,.stp,.f3d';
}

/**
 * Consume files provided by the user via an input form element. Called by the parent class (UploadStateMachine).
 * @param {Array} array, the File objects to be uploaded (note: this function assumes these are actual File objects w/ .name's, not Blob's).
 */
ModelUploadStateMachine.prototype.consumeFiles = function(array) {
    if(array.length > this.maxFilesAtOnce){
        var error = new Error("Upload failed (too many files).");
        logError("Package Model", error, logError.criticality.MAJOR, "Limit for concurrent uploads is "+this.maxFilesAtOnce+" files.", true);
    } else {
        for(var i = 0; i < array.length; i++){
            this.consumeFile(array[i], null, (array.length == 1));
        }
    }
}

/**
 * Consume 3d model filedata 
 * @param  {Blob or File} fileData  Blob or File contains the 3d model object description as text in ob or step format. If this object was constructed w/ new, it should be a Blob; Safari (and maybe other browsers?) don't support upload of File objects created with new. See: https://stackoverflow.com/a/44131368
 * @param  {string} fileName The name of the 3D model file (including extension). If null and fileData.name is defined, that will be used instead.
 * @param  {boolean} setAsUsed If true 3d model would be saved in package data
 * @param  {boolean} summary Optional summary
 * @param  {boolean} description Optional description
 * @param  {boolean} isGenerated Optional 3d model is generated or not
 * @return {Promise} A promise
 */
ModelUploadStateMachine.prototype.consumeFile = function(fileData, fileName, setAsUsed, summary, description, isGenerated) {
    var self = this;
    
    if(!fileData) return;

    //if file upload is requested in wizard mode and model is not generated using package generator (isGenerated=false) do not proceed
    //this happens when a file is dragged into editor in wizard mode
    if(this.packageViewerModel.masterModel && this.packageViewerModel.masterModel.wizardMode()) {
        if(!isGenerated) return;
    }
 
    console.assert(!!fileName || !!fileData.name, "No name provided to consumeFile().");

    if (!fileName && fileData.name) fileName = fileData.name;
    var fileExtension = fileName.split('.').pop().toLowerCase();
    var assetName = fileName.split('.')[0];

    this.processModel.addLoad("Creating 3D Model", "Creating 3D Model...");

    if(this.getFileTypes().indexOf(fileExtension) != -1) {
        // commented this out because:
        // 1. should not hardcode to a name,
        // 2. need to update resource also
        // if(self.root.data.metaData().name() == "The Unnamed Package"){
        //     self.root.rec();
        //     self.root.data.metaData().name(assetName);

        //     self.root.stopRec();
        // }

        return self.dataManager.checkModelExists(fileData)
        .then(function(status){
            // if status exists, create a reference to the already created asset id
            if(status.exists){
                var assetInfo = {
                    id: status.assetId,
                    type: status.assetType
                }

                return self.handleAsset(assetInfo, assetName, setAsUsed, isGenerated);
            } else {//Create new 3d Model resource on contentserver

                return self.dataManager.getResource("package", self.root.asset.id)
                .then(function(resource){
                    var basicResource = {
                        "name": assetName,
                        "summary": summary || "",
                        "description": description || "",
                        "tags": [ "3d Model", "Electronic" ],
                        "score": 0,
                        "private": resource.private,
                        "hub": resource.hub
                    };

                    return self.dataManager.createResource(basicResource, '3dmodel').then(function(asset){
                        var model3dAssetId = asset.id;
                        return self.dataManager.getResource('3dmodel', model3dAssetId)
                        .then(function(resource){
                            assetName = resource.name;
                            self.processModel.removeLoad("Creating 3D Model");
                            self.processModel.addLoad("Uploading " + assetName, "Uploading "+assetName+"...");
                            var assetInfo = {id: model3dAssetId, type: fileExtension};

                            var formData = new FormData();
                            formData.append(fileName, fileData, fileName);

                            return self.dataManager.updateResourceModel('3dmodel', model3dAssetId, formData)
                            .then(function(asset){
                                return self.handleAsset(assetInfo, assetName, setAsUsed, isGenerated);
                            })
                        })
                        .catch(function(err){
                                //// IMPORTANT TODO /// 
                                //  delete the new resource here
                                throw err;
                        });
                    }) 
                })
            }
        })
        .catch(function(err){
            // make sure thrown error goes to here
            self.processModel.removeLoad("Creating 3D Model");
            self.processModel.removeLoad("Uploading " + assetName);
            var details = "Unknown reason for failure.";
            if(err.reason) details = err.reason;
            logError("Package State Machine", err, logError.criticality.MAJOR, details);
        });
    } else {
        // not technically a translation error
        self.processModel.removeLoad("Creating 3D Model");
        var error = new Error("Failed to convert model file.");
        logError("Package Model", error, logError.criticality.MAJOR, "File type not supported.", true);
        self.processModel.removeLoad("Uploading " + assetName);
        clearTimeout(self.poll);
        return Promise.reject(error);
    }
};

ModelUploadStateMachine.prototype.handleAsset = function(asset, assetName, setAsUsed, isGenerated){
    var assetID = asset.id;
    var assetType = asset.type;

    var newModelRef = new cio_datamodel.Model3DReference(assetType, assetID);
    newModelRef.root = this.root;

    return this.pollUntilComplete(newModelRef, assetName, setAsUsed, isGenerated);
}


ModelUploadStateMachine.prototype._delayedPromise = function(fn, timeout) {
    return new Promise((res,rej) => this.poll = setTimeout(() => res(fn()), timeout));
}

ModelUploadStateMachine.prototype.pollUntilObjComplete = function(newModelRef, assetName){
    var self = this;
    // poll.  if done, return.  else recursive?
    return newModelRef.getConversionStatus('obj')
        .then(function(info){
            if(info.status == "translating" || info.status == "missing"){
                if(info.progress == "complete"){
                     return;
                } else {
                    self.processModel.changeDetails("Uploading " + assetName, "Uploading "+assetName+"... Converting 3D Model.");
                }

                return self._delayedPromise(function(){
                    return self.pollUntilObjComplete(newModelRef, assetName);
                }, 1000);

            } else if(info.status == "complete"){
                return;
            } else {
                // something went wrong
                var error =  Error("Translation failed for '"+ assetName+". (AssetID = "+newModelRef.assetId()+")");
                error.reason = (info.fail_cause)? info.fail_cause : "";
                throw error;
            }
        })
}
ModelUploadStateMachine.prototype.pollUntilF3dComplete = function(newModelRef, assetName){
    var self = this;
    // poll.  if done, return.  else recursive?
    return newModelRef.getConversionStatus('f3d')
        .then(function(info){
            if(info.status == "translating" || info.status == "missing"){
                if(info.progress == "complete"){
                    self.processModel.removeLoad("Uploading " + assetName);
                    return;
                } else {
                    self.processModel.changeDetails("Uploading " + assetName, "Uploading "+assetName+"... Converting 3D Model.");
                }

                return self._delayedPromise(function(){
                    return self.pollUntilF3dComplete(newModelRef, assetName);
                }, 1000);       

            } else if(info.status == "complete"){
                return;
            } else {
                // something went wrong
                var error =  Error("Translation failed for '"+ assetName+". (AssetID = "+newModelRef.assetId()+")");
                error.reason = (info.fail_cause)? info.fail_cause : "";
                throw error;
            }
        });
}


ModelUploadStateMachine.prototype.pollUntilComplete = function(newModelRef, assetName, setAsUsed, isGenerated){
    var self = this;
    return this.pollUntilF3dComplete(newModelRef, assetName)
        .then(function() {
            self.processModel.removeLoad("Uploading " + assetName);

            if(setAsUsed) {

                self.processModel.addLoad("GeomertryLoad", "Loading 3D model...");
                return newModelRef.calculateGeometryInfo( )
                .then(function( ){
                    self.processModel.removeLoad("GeomertryLoad", "Loading 3D model...");

                    self.root.rec();
                    
                    if(self.root.data.ipcParametricData()) {
                        self.root.data.isIPCDataAnd3DModelOutOfSync(!isGenerated);
                        self.root.data.is3dModelUploadedManually(!isGenerated);
                    }

                    self.root.data.model3d(newModelRef); //TODO: probably return the reference to the caller instead of storing in persistent data here?
                    
                    self.root.stopRec();

                })
                .catch(function(reason){
                    self.processModel.removeLoad("GeomertryLoad");
                    throw new Error("ThreeJS import failed for '"+assetName+"'' because: " + reason);
                });
            } else {
                 // do nothing
            }  
        });
}


module.exports = ModelUploadStateMachine;
